import type { NotificationInboxItemDto } from "../../lib/api";
import type { TranslationFunction } from "../../lib/preferences";
import type {
  ProviderNotificationCategory,
  ProviderNotificationViewItem,
} from "./ProviderNotificationInboxView";
import type { ProviderNotificationTargetAvailability } from "./provider-notification-target";

export interface ProviderNotificationServerItem {
  id: string;
  createdAtMs: number;
  read: boolean;
  category: ProviderNotificationCategory;
  eventType: string;
  target: ProviderNotificationTargetAvailability;
  dto: NotificationInboxItemDto;
}

function titleKey(eventType: string) {
  switch (eventType) {
    case "booking.requested.v1": return "New booking request";
    case "booking.confirmed.v1": return "Booking confirmed";
    case "booking.rejected.v1": return "Booking declined";
    case "booking.cancelled.v1": return "Booking cancelled";
    case "booking.expired.v1": return "Booking request expired";
    case "session.started.v1": return "Session started";
    case "session.stopped.v1": return "Session ended";
    case "session.extended.v1": return "Session extended";
    case "session.extension_requested.v1": return "extensions.notification";
    case "food_order.created.v1": return "New food order";
    case "food_order.preparing.v1": return "Order being prepared";
    case "food_order.ready.v1": return "Order ready to serve";
    case "food_order.delivered.v1": return "Order served";
    case "food_order.cancelled.v1": return "Food order cancelled";
    case "provider.approved.v1": return "Your account was approved";
    case "provider.rejected.v1": return "Your registration was declined";
    case "provider.suspended.v1": return "Your account was suspended";
    case "venue.approved.v1": return "Venue approved";
    case "venue.rejected.v1": return "Venue declined";
    case "venue.removed.v1": return "Venue removed";
    case "venue.removal_declined.v1": return "Venue removal declined";
    case "account.password_changed.v1": return "Password changed";
    case "account.email_changed.v1": return "Email changed";
    default: return "Notifications";
  }
}

type Params = NotificationInboxItemDto["template_params"];

function text(params: Params, key: string) {
  const value = params?.[key];
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function formatStart(params: Params, language: string) {
  const startsAt = text(params, "starts_at");
  if (!startsAt) return null;
  const date = new Date(startsAt);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(language === "ar" ? "ar-EG" : "en-US", {
      timeZone: text(params, "timezone") ?? undefined,
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  } catch {
    return null;
  }
}

/**
 * One line saying who, where and what: "Demo Player · PS5 Room A · Mon 28 Sep, 2:00 PM".
 * Built from the details the API stores on provider notifications; older rows have none.
 */
function detailLine(item: ProviderNotificationServerItem, t: TranslationFunction, language: string) {
  const params = item.dto.template_params ?? {};
  const by = text(params, "by");
  const parts: Array<string | null> = [];
  if (item.eventType.startsWith("booking.")) {
    parts.push(text(params, "guest"), text(params, "unit"), formatStart(params, language));
  } else if (item.eventType.startsWith("food_order.")) {
    parts.push(text(params, "unit"), text(params, "items"));
    const reason = text(params, "reason");
    if (reason) parts.push(reason);
    if (params.from_app === true) parts.push(t("from the app"));
  } else if (item.eventType.startsWith("session.")) {
    parts.push(text(params, "unit"), text(params, "guest"));
    if (item.eventType === "session.extension_requested.v1") parts.push(t("extensions.pendingHint"));
  }
  if (by) parts.push(t("by {name}", { name: by }));
  const line = parts.filter(Boolean).join(" · ");
  return line || undefined;
}

function targetKind(item: ProviderNotificationServerItem): ProviderNotificationViewItem["target"] {
  if (item.target.availability === "unavailable") return item.target;
  switch (item.target.target.kind) {
    case "booking": return { availability: "available", kind: "booking" };
    case "session": return { availability: "available", kind: "session" };
    case "food-order": return { availability: "available", kind: "food-order" };
    case "venue": return { availability: "available", kind: "venue" };
    case "pending-confirmations": return { availability: "available", kind: "pending-confirmations" };
    case "inbox": return { availability: "available", kind: "inbox" };
  }
}

/** Keeps server event strings out of the UI while using the existing translation function. */
export function mapProviderNotificationToViewItem(
  item: ProviderNotificationServerItem,
  t: TranslationFunction,
  language = "en",
): ProviderNotificationViewItem {
  return {
    body: detailLine(item, t, language),
    category: item.category,
    createdAtMs: item.createdAtMs,
    id: item.id,
    read: item.read,
    target: targetKind(item),
    title: t(titleKey(item.eventType)),
  };
}
